import { useCallback, useState, type ReactElement } from 'react';
import { useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { Button } from '../../../ui-kit/Button/Button';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Dropzone } from '../../../ui-kit/Dropzone/Dropzone';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { BareInput, Checkbox } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { MediaGrid } from './MediaGrid/MediaGrid';
import { MediaThumb } from './MediaThumb/MediaThumb';
import { UPLOAD_ACCEPT } from './model/uploads';
import { UploadList } from './UploadList/UploadList';
import { useUploads } from './use-uploads';

export interface MediaPickerDialogProps {
  blogId: string;
  alreadyAttached: readonly string[];
  onClose(): void;
  onPick(mediaIds: string[]): void;
}

/** POST-UC-3: выбор медиа из библиотеки (или загрузка) для прикрепления к посту. */
export function MediaPickerDialog({ blogId, alreadyAttached, onClose, onPick }: MediaPickerDialogProps): ReactElement {
  const { api } = useServices();
  const { t, tp } = useT();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const list = useResource(() => api.media.list(blogId, { q }), [api, blogId, q]);
  const onUploaded = useCallback(
    (media: { id: string }) => {
      setSelected((current) => (current.includes(media.id) || alreadyAttached.includes(media.id) ? current : [...current, media.id]));
      void list.reload();
    },
    [alreadyAttached, list.reload],
  );
  const uploads = useUploads(blogId, onUploaded);

  return (
    <Dialog
      open
      wide
      title={t('media.picker.title')}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <span className="u-muted u-grow">{tp('media.picker.selected', selected.length)}</span>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={selected.length === 0} onClick={() => onPick(selected)}>
            {t('media.picker.attach')}
          </Button>
        </>
      }
    >
      <div className="u-stack">
        <Dropzone accept={UPLOAD_ACCEPT} label={t('media.library.upload')} hint={t('media.library.uploadHint')} onFiles={(files) => void uploads.upload(files)} />
        <UploadList items={uploads.items} onClear={uploads.clear} />
        <BareInput label={t('media.library.search')} placeholder={t('media.library.search')} type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        {list.loading && !list.data ? (
          <Loading label={t('common.loading')} />
        ) : !list.data || list.data.length === 0 ? (
          <EmptyState title={t('media.library.empty')} />
        ) : (
          <MediaGrid
            label={t('media.picker.title')}
            items={list.data}
            renderItem={(media) => {
              const attached = alreadyAttached.includes(media.id);
              return (
                <>
                  <MediaThumb media={media} />
                  <Checkbox
                    label={attached ? `${media.originalName} · ${t('media.picker.inPost')}` : media.originalName}
                    disabled={attached}
                    checked={attached || selected.includes(media.id)}
                    onChange={(e) => setSelected(e.target.checked ? [...selected, media.id] : selected.filter((id) => id !== media.id))}
                  />
                </>
              );
            }}
          />
        )}
      </div>
    </Dialog>
  );
}
